import type { Facing } from '@/engine/math';
import type { FloorId } from '@/world/types';
import type { FriendDef } from './roster';
import { buildCharacter, type CharSprites } from '@/gfx/characters';
import type { InvItem, ItemKind } from './items';

export type CharState = 'outside' | 'free' | 'hidden' | 'groggy' | 'carried' | 'captured' | 'escaped';
export type FollowMode = 'follow' | 'wait' | 'hide';
export type Gait = 'sneak' | 'walk' | 'run';

export interface Character {
  id: string;
  def: FriendDef;
  sprites: CharSprites;
  floor: FloorId;
  x: number;
  y: number;
  facing: Facing;
  state: CharState;
  mode: FollowMode;
  hideProp: string | null;
  /** where to step back out to when leaving a hiding place */
  hideReturn: { x: number; y: number } | null;
  groggy: number;
  anim: number;
  moving: boolean;
  gait: Gait;
  flashOn: boolean;
  battery: number;
  flashAngle: number;
  inventory: InvItem[];
  path: Array<[number, number]> | null;
  pathTimer: number;
  stepTimer: number;
  /** room the character is imprisoned in */
  prisonRoom: string | null;
  /** cooldown before the same follower re-paths after a door */
  stuck: number;
  /** last known leader floor/pos target for followers on other floors */
  travelTo: { floor: FloorId; stairsId: string } | null;
  bubble: { text: string; t: number } | null;
  /** how long since a footstep noise */
  noiseLevel: number;
}

export function makeCharacter(def: FriendDef): Character {
  return {
    id: def.id, def, sprites: buildCharacter(def.look),
    floor: 'ground', x: 0, y: 0, facing: 'up',
    state: 'outside', mode: 'follow', hideProp: null, hideReturn: null, groggy: 0, anim: 0, moving: false, gait: 'walk',
    flashOn: false, battery: 1, flashAngle: -Math.PI / 2,
    inventory: [], path: null, pathTimer: 0, stepTimer: 0, prisonRoom: null, stuck: 0, travelTo: null, bubble: null, noiseLevel: 0,
  };
}

export type MonsterState = 'patrol' | 'investigate' | 'chase' | 'search' | 'carry' | 'stunned' | 'lair' | 'pull';

export interface Monster {
  floor: FloorId;
  x: number;
  y: number;
  facing: Facing;
  state: MonsterState;
  carrying: string[];
  target: string | null;
  lastSeen: { floor: FloorId; x: number; y: number } | null;
  /** Seconds since the current target was actually visible. */
  sightAge: number;
  /** Only set when the monster sees someone enter this hiding place. */
  witnessedHide: string | null;
  poi: { floor: FloorId; x: number; y: number } | null;
  path: Array<[number, number]> | null;
  pathTimer: number;
  patrolIdx: number;
  wait: number;
  stun: number;
  anim: number;
  attackTimer: number;
  searchTimer: number;
  /** suspicion 0..1 */
  aware: number;
  /** id of the prop it's pulling a friend out of */
  pullProp: string | null;
  speedMul: number;
  /** growl cooldown */
  growl: number;
  /** last time it saw a flashlight */
  lightSeen: number;
  /** after a blocked straight-line move, follow the path for a while */
  directBlock: number;
  passageCooldown: number;
  passageCheck: number;
  passagePlan: { from: string; to: string; seconds: number; goal: { floor: FloorId; x: number; y: number } } | null;
  transit: { from: string; to: string; seconds: number; phase: 'enter' | 'cross' | 'emerge'; t: number; held: number; warned: boolean } | null;
  footstepDistance: number;
  footstepCue: number;
  doorWait: { id: string; floor: FloorId; t: number; x: number; y: number } | null;
  /** Quiet periods between nearby patrols; unrelated to visual/hearing suspicion. */
  quietTime: number;
  pressureCooldown: number;
  /** Game time of the last sighting, so two sightings give an observed velocity. */
  seenAt: number | null;
  /** Observed velocity of the last target (px/s), from sightings only. */
  seenVel: { vx: number; vy: number } | null;
  /** Where and when it last saw or clearly heard someone; patrols linger there for a while. */
  lastContact: { floor: FloorId; x: number; y: number; t: number } | null;
  /** Ordered places to check after losing a chase: nearby cover, exits, then wandering. */
  searchPlan: SearchStep[] | null;
  searchStep: SearchStep | null;
  /** Seconds left standing at a search step, looking. */
  lookTimer: number;
  /** A search has already announced that it is checking cover. */
  searchCued: boolean;
}

export interface SearchStep { x: number; y: number; kind: 'hide' | 'door' | 'wander'; prop?: string; look: number }

export function makeMonster(): Monster {
  return {
    floor: 'ground', x: 0, y: 0, facing: 'down', state: 'patrol', carrying: [], target: null, lastSeen: null, sightAge: 999, witnessedHide: null, poi: null,
    path: null, pathTimer: 0, patrolIdx: 0, wait: 0, stun: 0, anim: 0, attackTimer: 0, searchTimer: 0, aware: 0, pullProp: null, speedMul: 1, growl: 0, lightSeen: 0, directBlock: 0,
    passageCooldown: 20, passageCheck: 0, passagePlan: null, transit: null,
    footstepDistance: 0, footstepCue: 0,
    doorWait: null,
    quietTime: -20, pressureCooldown: 0,
    seenAt: null, seenVel: null, lastContact: null, searchPlan: null, searchStep: null, lookTimer: 0, searchCued: false,
  };
}

export interface GroundItem {
  id: string;
  kind: ItemKind;
  floor: FloorId;
  x: number;
  y: number;
  count: number;
  text?: string;
  /** id of the friend whose dropped bag this is; `contents` then holds what is inside */
  bag?: string;
  contents?: InvItem[];
}

export interface Projectile {
  kind: 'bottle' | 'firecracker';
  floor: FloorId;
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  fuse: number;
  landed: boolean;
}

export interface Noise {
  floor: FloorId;
  x: number;
  y: number;
  r: number;
  t: number;
  /** 'step' noises are ignored by the HUD ring */
  kind: 'step' | 'door' | 'impact' | 'bang' | 'search' | 'rattle' | 'scream';
  from?: string;
}

export interface Effect {
  kind: 'ring' | 'bang' | 'flash' | 'stars' | 'sparkle' | 'text' | 'dust';
  floor: FloorId;
  x: number; y: number;
  t: number;
  dur: number;
  r?: number;
  text?: string;
  color?: string;
  angle?: number;
}
